/**
 * Card Detector for Blog Posts
 * Automatically detects card names in [[brackets]] and displays product information
 */

class CardDetector {
  constructor() {
    this.searchEndpoint = '/search?view=deck-builder&type=product';
    this.cache = new Map();
    this.processing = false;
    this.detectedCards = new Map(); // Track all detected cards for gallery
  }

  /**
   * Initialize the card detector
   */
  async init() {
    console.log('[CardDetector] init() called');
    const articleContent = document.querySelector('.article-template__content');
    
    if (!articleContent) {
      console.log('[CardDetector] No article content found (.article-template__content)');
      return;
    }

    console.log('[CardDetector] Article content found, processing...');
    // Find all card references [[card name]]
    await this.processContent(articleContent);
    
    // Create gallery at the end if cards were detected
    if (this.detectedCards.size > 0) {
      this.createCardGallery(articleContent);
    }
  }

  /**
   * Process the content and replace card references
   */
  async processContent(container) {
    if (this.processing) {
      console.log('[CardDetector] Already processing, skipping...');
      return;
    }
    this.processing = true;

    // Regex to find [[card name]] or [[card name]][SIZE]
    const cardPattern = /\[\[([^\]]+)\]\](?:\[([A-Z]+)\])?/g;
    
    console.log('[CardDetector] Scanning for card references...');
    
    // Get all text nodes
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      if (cardPattern.test(node.textContent)) {
        textNodes.push(node);
        console.log('[CardDetector] Found text node with cards:', node.textContent.substring(0, 100));
      }
      cardPattern.lastIndex = 0; // Reset regex
    }

    console.log(`[CardDetector] Found ${textNodes.length} text nodes with card references`);

    // Process each text node
    for (const textNode of textNodes) {
      await this.replaceCardReferences(textNode);
    }

    console.log('[CardDetector] Processing complete');
    this.processing = false;
  }

  /**
   * Replace card references in a text node
   */
  async replaceCardReferences(textNode) {
    const cardPattern = /\[\[([^\]]+)\]\](?:\[([A-Z]+)\])?/g;
    const text = textNode.textContent;
    const matches = [...text.matchAll(cardPattern)];

    if (matches.length === 0) return;

    // Create a document fragment to hold the new content
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    for (const match of matches) {
      const cardName = match[1].trim();
      const size = match[2] || 'MD'; // Default to MD (medium)
      const matchStart = match.index;
      const matchEnd = matchStart + match[0].length;

      // Add text before the match
      if (matchStart > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.substring(lastIndex, matchStart))
        );
      }

      // Create and add the card element
      const cardElement = await this.createCardElement(cardName, size);
      fragment.appendChild(cardElement);

      lastIndex = matchEnd;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }

    // Replace the text node with the fragment
    textNode.parentNode.replaceChild(fragment, textNode);
  }

  /**
   * Create a card element for a given card name
   */
  async createCardElement(cardName, size = 'MD') {
    // Check cache first
    if (this.cache.has(cardName)) {
      const product = this.cache.get(cardName);
      // Track for gallery
      if (!this.detectedCards.has(cardName)) {
        this.detectedCards.set(cardName, product);
      }
      return this.buildCardHTML(product, size);
    }

    // Search for the product
    try {
      const product = await this.searchProduct(cardName);
      
      if (product) {
        this.cache.set(cardName, product);
        // Track for gallery
        if (!this.detectedCards.has(cardName)) {
          this.detectedCards.set(cardName, product);
        }
        return this.buildCardHTML(product, size);
      } else {
        // Product not found, return plain text
        return document.createTextNode(`[[${cardName}]]`);
      }
    } catch (error) {
      console.error('Error searching for card:', cardName, error);
      return document.createTextNode(`[[${cardName}]]`);
    }
  }

  /**
   * Search for a product by name
   */
  async searchProduct(cardName) {
    try {
      const query = encodeURIComponent(cardName);
      const response = await fetch(`${this.searchEndpoint}&q=${query}`);
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if we have results
      if (!data.results || data.results.length === 0) {
        console.log(`No results found for: ${cardName}`);
        return null;
      }

      // Get the first product
      const productData = data.results[0];
      
      // Format price (convert from cents to dollars)
      const priceFormatted = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
      }).format(productData.price / 100);

      // Get first available variant
      const firstVariant = productData.variants && productData.variants.length > 0 
        ? productData.variants[0] 
        : null;

      // Build product object
      const product = {
        title: productData.title,
        url: `/products/${productData.handle}`,
        image: productData.featured_image?.url || '',
        price: priceFormatted,
        available: productData.available,
        variantId: firstVariant ? firstVariant.id : null
      };

      console.log(`Found product for ${cardName}:`, product);
      return product;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  /**
   * Build the HTML for a card element
   */
  buildCardHTML(product, size = 'MD') {
    const wrapper = document.createElement('span');
    wrapper.className = 'inline-card-wrapper';

    const card = document.createElement('a');
    card.href = product.url;
    card.className = `inline-card inline-card--${size.toLowerCase()}`;
    card.setAttribute('data-card-name', product.title);
    card.setAttribute('data-size', size);

    card.innerHTML = `
      <span class="inline-card__image">
        ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : ''}
      </span>
      <span class="inline-card__info">
        <span class="inline-card__title">${product.title}</span>
        ${product.price ? `<span class="inline-card__price">${product.price}</span>` : ''}
        ${!product.available ? '<span class="inline-card__badge">Agotado</span>' : ''}
      </span>
      <span class="inline-card__icon">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;

    wrapper.appendChild(card);
    return wrapper;
  }

  /**
   * Create gallery of all detected cards at the end of the article
   */
  createCardGallery(container) {
    console.log('[CardDetector] Creating gallery with', this.detectedCards.size, 'cards');
    
    const gallery = document.createElement('div');
    gallery.className = 'cards-gallery';
    gallery.innerHTML = `
      <div class="cards-gallery__header">
        <h2 class="cards-gallery__title">Cartas Mencionadas en Este Artículo</h2>
        <p class="cards-gallery__subtitle">${this.detectedCards.size} ${this.detectedCards.size === 1 ? 'carta' : 'cartas'}</p>
      </div>
      <div class="cards-gallery__grid"></div>
    `;

    const grid = gallery.querySelector('.cards-gallery__grid');

    // Add each card to the gallery
    for (const [cardName, product] of this.detectedCards) {
      const galleryCard = this.buildGalleryCardHTML(product);
      grid.appendChild(galleryCard);
    }

    // Append gallery after the article content
    container.parentNode.insertBefore(gallery, container.nextSibling);
  }

  /**
   * Build HTML for a gallery card with add to cart
   */
  buildGalleryCardHTML(product) {
    const card = document.createElement('div');
    card.className = 'gallery-card';
    
    // Get the first available variant
    const variantId = product.variantId || null;
    const available = product.available;

    card.innerHTML = `
      <a href="${product.url}" class="gallery-card__link">
        <div class="gallery-card__image">
          ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : ''}
        </div>
        <div class="gallery-card__info">
          <h3 class="gallery-card__title">${product.title}</h3>
          <p class="gallery-card__price">${product.price}</p>
        </div>
      </a>
      ${available && variantId ? `
        <button class="gallery-card__add-to-cart button button--secondary" data-variant-id="${variantId}" data-product-title="${product.title}">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="6" cy="13" r="1"></circle>
            <circle cx="12" cy="13" r="1"></circle>
            <path d="M1 1h3l1.68 8.39a2 2 0 002 1.61h6.74a2 2 0 001.95-1.57L14 4H4"></path>
          </svg>
          <span>Agregar al Carrito</span>
        </button>
      ` : `
        <button class="gallery-card__add-to-cart button button--secondary" disabled>
          <span>Agotado</span>
        </button>
      `}
    `;

    // Add event listener for add to cart
    if (available && variantId) {
      const addButton = card.querySelector('.gallery-card__add-to-cart');
      addButton.addEventListener('click', (e) => this.addToCart(e, variantId, product.title));
    }

    return card;
  }

  /**
   * Add product to cart
   */
  async addToCart(event, variantId, productTitle) {
    event.preventDefault();
    const button = event.currentTarget;
    const originalHTML = button.innerHTML;

    // Show loading state
    button.disabled = true;
    button.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" class="spinner">
        <circle cx="8" cy="8" r="6"></circle>
      </svg>
      <span>Agregando...</span>
    `;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (response.ok) {
        // Success!
        button.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M13 4L6 11L3 8"></path>
          </svg>
          <span>¡Agregado!</span>
        `;
        button.classList.add('added');

        // Trigger cart update event
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));

        // Reset button after 2 seconds
        setTimeout(() => {
          button.innerHTML = originalHTML;
          button.disabled = false;
          button.classList.remove('added');
        }, 2000);

      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.innerHTML = `
        <span>Error</span>
      `;
      setTimeout(() => {
        button.innerHTML = originalHTML;
        button.disabled = false;
      }, 2000);
    }
  }
}

// Initialize when DOM is ready
console.log('[CardDetector] Script loaded');

if (document.readyState === 'loading') {
  console.log('[CardDetector] Waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[CardDetector] DOM loaded, initializing...');
    const detector = new CardDetector();
    detector.init();
  });
} else {
  console.log('[CardDetector] DOM already loaded, initializing...');
  const detector = new CardDetector();
  detector.init();
}

